import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  ClaimNote,
  ClaimNoteType,
  createClaimNote,
  getClaimNotes,
} from '../services/claimsApi';
import { claimColors, claimSx } from '../styles/claimPageStyles';

type ComposeTab = 'internal' | 'message_to_tenant' | 'message_to_manager';

const COMPOSE_TABS: { id: ComposeTab; label: string; noteType: ClaimNoteType }[] = [
  { id: 'internal', label: 'Add Internal note', noteType: 'internal' },
  { id: 'message_to_tenant', label: 'Send Message to Tenant', noteType: 'message_to_tenant' },
  { id: 'message_to_manager', label: 'Send Message to Facility Manager', noteType: 'message_to_manager' },
];

/** Facility manager messaging hidden for now; timeline still shows existing manager notes. */
const VISIBLE_COMPOSE_TABS = COMPOSE_TABS.filter((tab) => tab.id !== 'message_to_manager');

const formatNoteDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeStr = date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
  return `${dateStr} ${timeStr}`;
};

const avatarInitial = (label: string): string => {
  const trimmed = label.trim();
  if (!trimmed) return '?';
  return trimmed.charAt(0).toUpperCase();
};

interface ClaimCommunicationsProps {
  claimId: string;
}

const ClaimCommunications: React.FC<ClaimCommunicationsProps> = ({ claimId }) => {
  const [notes, setNotes] = useState<ClaimNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState<ComposeTab>('internal');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadNotes = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const data = await getClaimNotes(claimId);
        if (!cancelled) {
          setNotes(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setLoadError('Unable to load claim communications.');
          setNotes([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadNotes();

    return () => {
      cancelled = true;
    };
  }, [claimId]);

  const handleTabChange = (_: React.SyntheticEvent, value: ComposeTab) => {
    setActiveTab(value);
    setSaveError('');
    setSaveSuccess('');
  };

  const handleCancel = () => {
    setDraft('');
    setSaveError('');
    setSaveSuccess('');
  };

  const handleSubmit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;

    const tab = VISIBLE_COMPOSE_TABS.find((t) => t.id === activeTab);
    if (!tab) return;

    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const created = await createClaimNote(claimId, trimmed, tab.noteType);
      setNotes((prev) => [...prev, created]);
      setDraft('');
      if (tab.noteType === 'message_to_tenant') {
        setSaveSuccess(
          created.tenant_email_sent
            ? 'Message saved and emailed to the tenant.'
            : 'Message saved. Email was not sent (tenant email missing or emails disabled).'
        );
      }
    } catch {
      setSaveError('Unable to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const submitLabel = activeTab === 'message_to_tenant' ? 'Send' : 'Save';
  const submittingLabel = activeTab === 'message_to_tenant' ? 'Sending…' : 'Saving…';

  return (
    <Paper elevation={0} sx={{ ...claimSx.sectionCard, flex: '1 1 100%', p: 3 }}>
      <Typography variant="h6" sx={claimSx.sectionTitle}>
        Communications
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : loadError ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
      ) : (
        <Stack spacing={0} sx={{ mb: 3 }}>
          {notes.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
              No communications yet.
            </Typography>
          ) : (
            notes.map((note, index) => (
              <Box
                key={note.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  py: 2,
                  borderBottom: index < notes.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: claimColors.navy,
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {avatarInitial(note.sender_label)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary' }}>
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {note.sender_label}
                    </Box>
                    {' — '}
                    {formatNoteDateTime(note.created_at)}
                    {note.note_type_label && note.note_type !== 'state_change' && (
                      <>
                        {' · '}
                        <Box component="span" sx={{ fontStyle: 'italic' }}>
                          {note.note_type_label}
                        </Box>
                      </>
                    )}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                  >
                    {note.note}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Stack>
      )}

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 48,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: claimColors.navy,
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: claimColors.red,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: claimColors.red,
              height: 3,
            },
          }}
        >
          {VISIBLE_COMPOSE_TABS.map((tab) => (
            <Tab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TextField
            multiline
            minRows={6}
            fullWidth
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Enter your note..."
            disabled={saving}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.default',
              },
            }}
          />

          {saveError && (
            <Typography color="error" variant="body2" sx={{ mb: 1.5 }}>
              {saveError}
            </Typography>
          )}

          {saveSuccess && (
            <Typography color="success.main" variant="body2" sx={{ mb: 1.5 }}>
              {saveSuccess}
            </Typography>
          )}

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleSubmit}
              disabled={saving || !draft.trim()}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {saving ? submittingLabel : submitLabel}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelOutlinedIcon />}
              onClick={handleCancel}
              disabled={saving || !draft}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.primary',
                borderColor: 'divider',
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};

export default ClaimCommunications;
